import { Head, useForm } from "@inertiajs/react"
import { Button, Text, TextField } from "@radix-ui/themes"
import { LoaderCircle } from "lucide-react"
import type { FormEventHandler } from "react"

import InputError from "@/components/input-error"
import TextLink from "@/components/text-link"
import CenteredLayout from "@/layouts/centered-layout"
import { signInPath, signUpPath } from "@/routes"

interface RegisterForm {
  name: string
  email: string
  password: string
  password_confirmation: string
}

export default function Register() {
  const { data, setData, post, processing, errors, reset } = useForm<Required<RegisterForm>>({
    name: "",
    email: "",
    password: "",
    password_confirmation: "",
  })

  const submit: FormEventHandler = (e) => {
    e.preventDefault()
    post(signUpPath(), {
      onFinish: () => reset("password", "password_confirmation"),
    })
  }

  return (
    <CenteredLayout
      title="Create an account"
      description="Enter your details below to create your account"
    >
      <Head title="Register" />
      <form className="flex flex-col gap-6" onSubmit={submit}>
        <div className="grid gap-6">
          <div className="grid gap-2">
            <Text as="label" htmlFor="name" size="2" weight="medium">
              Name
            </Text>
            <TextField.Root
              id="name"
              type="text"
              required
              autoFocus
              tabIndex={1}
              autoComplete="name"
              value={data.name}
              onChange={(e) => setData("name", e.target.value)}
              placeholder="Full name"
            />
            <InputError message={errors.name} />
          </div>

          <div className="grid gap-2">
            <Text as="label" htmlFor="email" size="2" weight="medium">
              Email address
            </Text>
            <TextField.Root
              id="email"
              type="email"
              required
              tabIndex={2}
              autoComplete="email"
              value={data.email}
              onChange={(e) => setData("email", e.target.value)}
              placeholder="email@example.com"
            />
            <InputError message={errors.email} />
          </div>

          <div className="grid gap-2">
            <Text as="label" htmlFor="password" size="2" weight="medium">
              Password
            </Text>
            <TextField.Root
              id="password"
              type="password"
              required
              tabIndex={3}
              autoComplete="new-password"
              value={data.password}
              onChange={(e) => setData("password", e.target.value)}
              placeholder="Password"
            />
            <InputError message={errors.password} />
          </div>

          <div className="grid gap-2">
            <Text as="label" htmlFor="password_confirmation" size="2" weight="medium">
              Confirm password
            </Text>
            <TextField.Root
              id="password_confirmation"
              type="password"
              required
              tabIndex={4}
              autoComplete="new-password"
              value={data.password_confirmation}
              onChange={(e) => setData("password_confirmation", e.target.value)}
              placeholder="Confirm password"
            />
            <InputError message={errors.password_confirmation} />
          </div>

          <Button type="submit" className="mt-4 w-full" tabIndex={5} disabled={processing}>
            {processing && <LoaderCircle className="h-4 w-4 animate-spin" />}
            Create account
          </Button>
        </div>

        <div className="text-muted-foreground text-center text-sm">
          Already have an account?{" "}
          <TextLink href={signInPath()} tabIndex={6}>
            Log in
          </TextLink>
        </div>
      </form>
    </CenteredLayout>
  )
}
